import { useObservable } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { hasNexusPermission } from '@nsc/nexus-permissions/nexus-api/nexus-permission-gate.js';
import { subscribePlatformRoleAssignmentFor } from '@nsc/nexus-permissions/nexus-api/platform-gated-reads.js';
import type { NexusPermission } from '@nsc/nexus-permissions/platform-rbac/nexus-permissions.js';
import { currentUser } from '@adornis/users/client/currentUser.js';

/** Server-backed Nexus permission check (avoids client IndexedDB role cache used by `useABAC`). */
export function useNexusPermissionAccess(
  permission: NexusPermission,
  resourceContext?: string,
): { can: boolean; loading: boolean } {
  const user = useObservable(() => currentUser(), []);
  const { value: assignment } = useQuery(
    () =>
      user?._id
        ? subscribePlatformRoleAssignmentFor(user._id)({
            roleIds: 1,
            scope: { contexts: 1, permission: 1 },
          })
        : Promise.resolve(null),
    [user?._id],
  );
  const assignmentKey = assignment ? JSON.stringify(assignment) : '';
  const { value: can, loading } = useQuery(
    () => (user?._id ? hasNexusPermission(permission, resourceContext) : Promise.resolve(false)),
    [permission, resourceContext, user?._id, assignmentKey],
  );
  return { can: can === true, loading: !!loading || !user?._id };
}
